<template>
<div class="chapter">
<div class="inner_page">
<p class="C765"><strong><a id="page_19">1</a></strong></p><a id="page_19">
<p class="C773"><strong>In Search of the Roots of American Inequality Exceptionalism</strong></p>
<p class="C773">An Analysis Based on Luxembourg Income Study (LIS) Data</p>
<p class="C766">Janet C. Gornick, Branko Milanovic, and Nathaniel Johnson</p>
<p class="C845"><strong>1.1 Introduction</strong></p>
<p class="C846">1.1.1 Background</p>
</a><p class="P51"><a id="page_19">It has been known for at least two decades that disposable household income—income after accounting for transfers and taxes—is more unequally distributed in the United States than in comparable high-income economies (see, e.g., Brandolini and Smeeding 2006; Gornick and Jäntti 2013; OECD 2009, 2011; Piketty and Saez 2006). Broadly speaking, there are two possible underlying explanations. First, market income inequality (i.e., income before direct taxes and transfers are taken into account) may be similar in the US as elsewhere, but US taxes and transfers are less redistributive, either because the overall size of the welfare state is smaller or because the redistribution is less progressive. Second, market income inequality may itself be higher in the US than in many other countries, thus driving up the high level of inequality even after redistribution is taken into account. The first explanation has generally held sway because US market income inequality calculated across households—importantly, households of all ages—</a><a id="page_20">is not especially exceptional, across the OECD countries, while disposable income inequality is substantially greater.</a></p><a id="page_20">
<p class="P4">Recent work, however, by Gornick and Milanovic (2015) shifts that conclusion about the market income inequality in the US, in comparative perspective. They begin with the insight that market income inequality, when calculated across households of all ages, may be depressed—especially relative to many European countries—because Americans tend to stay in the labor market until later in life compared with their counterparts elsewhere. Because the market income in pensioners’ households is often very small or zero, the existence of a developed system of social protection paradoxically exaggerates <em>market</em> income inequality (among older households) in other OECD countries and brings the overall market income inequality in line with that reported in the US. Thus, the comparatively high level of US market income inequality—net of older households—is obscured. Gornick and Milanovic’s main conclusion is that, for persons under 60 years of age, weaker US redistribution is not the main cause of greater inequality at the disposable income stage. The “problem” is that the distribution of “original” labor and capital incomes is substantially more unequal in the US than elsewhere, and government redistribution, at the average OECD level, does not compensate for the inequality generated in the market.</p>
<p class="P4">Gornick and Milanovic’s (2015) analysis had precursors in the work of scholars of earnings distributions, who argued that weaker redistribution in the US could not alone explain the entire disposable income inequality gap between the US and the rest of the OECD countries. Mishel (2015), for example, argues that the underlying market income distribution, most importantly the earnings distribution, in the US, is highly unequal in cross-national terms. He and others point to, on the bottom end of the earnings distribution, the low US minimum wage and the high prevalence of low-paid jobs (Gautié and Schmitt 2009; Lucifora and Salverda 2009), and, on the upper end, the extremely high earnings of managers, doctors, lawyers, CEOs and the financial sector (Gabaix and Landler 2008). The exceptionally large gap between CEOs’ salaries in the US and in the rest of OECD countries is well documented (see Mishel and Davies 2015; Piketty 2014). Indeed, the findings in Gornick and Milanovic (2015) confirm that market income inequality is a major explanation for comparatively high levels of disposable income inequality in the US, among working-age households.</p>
<p class="C754">1.1.2 Objective</p>
</a><p class="P51"><a id="page_20">The objective of this chapter is to further investigate the nature of the high level of market income inequality found among US working-age households, compared to their counterparts in several other affluent countries. Because the major component of market income is labor income, we focus exclusively on it—disregarding income from capital, which is a relatively </a><a id="page_21">minor component in the market income package of working-age households in these countries.<sup></sup></a><span href="#/html/38_Notes#id_10" id="id_9" title="Link target outside of Sample">1</span></p>
<p class="P4">Our main strategy is to disaggregate working-age households—in the US and in the comparison countries—into household subgroups. These subgroups are distinguished by the number and gender of earners in the household, and (subsequently) by the partnership and parenting status of the household. Clearly, a household’s labor income is shaped by the number of earners present. The logic of further disaggregating by gender, partnership, and parenting is rooted in the labor economics literature, which has long established that individuals’ earnings (gross and net of other worker-and job-level characteristics) are affected by their gender and whether they have partners and/or children (for a review, see Blau and Winkler 2017).</p>
<p class="P4">We assess inequality that exists both within and between various household types and we compare the results for the US with those in other OECD countries. Our objective is to establish whether the greater underlying US market income inequality is the result of (1) higher earnings inequality within each of the relevant groups, (2) an unusual composition (for example, a high share of groups where earnings inequality is high), or (3) large gaps between groups in mean earnings.<sup><span href="#/html/38_Notes#id_8" id="id_7" title="Link target outside of Sample">2</span></sup></p>
<p class="P4">A substantial prior literature on economic inequality in the US addresses the question of the levels, and/or drivers, of within-group versus between-group inequality. Much of this literature focuses on earnings, and most of it locates the question of within-versus-between in the context of change over time.</p>
<p class="P4">Two decades ago, McCall (2000) observed that most research on (earnings) inequality in the US was concerned with growing gaps between groups—with workers differentiated by race, age, education, and income. She noted that, in fact, a large share of rising inequality had occurred within these groups. Her own study assessed variation in within-group inequality across 500 local labor markets. Western, Bloome, and Percheski (2008) assessed rising income inequality among US families, between 1975 and 2005. They concluded that most of the increase in family income inequality during that period was driven by rising within-group inequality; their disaggregation combined family type and educational attainment.</p>
<p class="P4">Introducing his own study of the drivers of within-group inequality between 1970 and 2001, VanHeuvelen (2018, 1–65) summarized the literature as follows: “An increasing number of studies have begun to note that within-group inequality—or the inequality that remains after accounting for average between-group pay differences . . . such as human capital, <a id="page_22">occupational characteristics, sex, socio-demographics, and household composition—is of growing importance for overall inequality.”</a></p><a id="page_22">
<p class="P4">While these and other earlier studies influenced our analytic strategy, our study is a departure. First, no earlier research disaggregates household types as we do. Our typology includes unusually finely drawn categories; our groups are defined by the number and gender of a household’s earners, further disaggregated by partnership and parenting status.</p>
<p class="P4">Second, we depart from earlier research with respect to our income measure and unit of analysis. Most existing within-versus-between research assesses either earnings at the individual level, or posttax, posttransfer income at the household (or family) level. In contrast, we focus on earnings (what we call market income) at the household level. Our framework allows us to place our work in the large cross-national literature, much of it using the same data that we use, concerned with the extent to which inequality in disposable household income is driven by inequality in household-level market income.</p>
<p class="C754">1.1.3 Analytic Strategy</p>
</a><p class="P51"><a id="page_22">To carry out our analyses, we use microdata, drawn from household surveys, contained in the Luxembourg Income Study (LIS) Database Wave VIII, which is centered on the year 2010.<sup></sup></a><span href="#/html/38_Notes#id_16" id="id_15" title="Link target outside of Sample">3</span> We include 24 OECD countries:<sup><span href="#/html/38_Notes#id_14" id="id_13" title="Link target outside of Sample">4</span></sup> Australia, Canada, Czech Republic, Denmark, Estonia, Finland, France, Germany, Greece, Hungary, Iceland, Ireland, Israel, Italy, Luxembourg, Netherlands, Norway, Poland, Russia, Slovakia, Slovenia, Spain, the UK, and the US.<sup><span href="#/html/38_Notes#id_12" id="id_11" title="Link target outside of Sample">5</span></sup> In all cases, but one, the data are from the year 2010; the exception is Hungary, for which we have 2009 data. Appendix <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_816">table 1A.1</a> reports the list of countries and datasets used.</p>
<p class="P4">Our analysis is conducted across households whose members are all below age 60 <em>and</em> which have at least one member reporting labor income. To assess labor income, we use LIS’s harmonized variable <em>hil</em> (that is, household income from labor). This variable includes: (1) cash wage and salary income, and the value of nonmonetary goods and services received as a substitute for cash; (2) monetary supplements to the basic wage and the value of nonmonetary supplements; (3) cash wage and salary income, and the value of nonmonetary goods and services, received by directors of their own enterprise; (4) monetary payments and the value of nonmonetary goods and services <a id="page_23">received from casual, irregular, or occasional dependent employment; and (5) profits/losses from self-employment activities.</a></p><a id="page_23">
</a><div class="C1136"><a id="page_23">
</a><p class="C1135"><a id="page_23"></a><a id="id_811"><img alt="" class="Image349" src="html/images/37_1.png"/></a></p><a id="id_811">
<p class="C743"><strong>Fig. 1.1 Inequality of labor income across working-age households, in 24 OECD countries</strong></p>
<p class="C1138"><em>Note</em>: Ginis based on equivalized labor income.</p>
</a></div><a id="id_811">
</a><p class="P4"><a id="id_811">Because one of our motivating interests is the relationship, at the household-level, between earnings inequality and disposable income inequality, our unit of observation is not an individual worker (earner) but the household. Total household earnings are adjusted for household size, using the well-known “square-root adjustment.” In other words, total household earnings are divided by the square root of the number of household members.<sup></sup></a><span href="#/html/38_Notes#id_18" id="id_17" title="Link target outside of Sample">6</span> Thus, we arrive at a variable that measures potential individual welfare (assuming equal division of earnings within the households) derived from labor income.</p>
<p class="P4">As our measure of inequality, we mainly use the Gini coefficient. The Gini is preferred largely because it enables us to easily relate our results about inequality within different demographic subgroups to the well-known Gini values of market and disposable income inequality seen in the US and elsewhere. In one part of our analysis, we use two Theil indices.</p>
<p class="C826"><strong>1.2 Labor Income Inequality across Various Household Types</strong></p>
<p class="P51">In <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_811">figure 1.1</a>, we report inequality, across households, of labor incomes. The four countries with the most unequal earnings distributions (at the <a id="page_24">household level) are Israel and three Anglophone countries; the US is ranked second highest. These labor income Ginis range from 0.277–0.311 for the highly egalitarian Slovenia and Slovakia, respectively, to 0.436–0.442 in the US and Israel. The median and mean labor income Gini is about 0.36. Thus, we establish immediately that labor income inequality in the US is, relative to other OECD countries, on the high end.</a></p><a id="page_24">
<p class="P4">What lies behind this comparatively high level of earnings inequality among US households? Our approach, as already mentioned, is to disaggregate working-age households into several demographic groups (defined below) and to assess labor income inequality within each of them.</p>
<p class="P4">The Gini decomposition when the population is divided into different groups has three terms: a weighted sum of within-group inequalities (narrowly defined within-inequality), inequality that is the result of differences in mean incomes between the groups, and an overlap (residual) term that reflects the homogeneity of the underlying populations. To understand the meaning of the last, note that when incomes of the groups into which we have divided the population are so different that there is absolutely no overlap (e.g., all individuals from a mean-richer group have higher incomes than all individuals from a mean-poorer group), the overlap term becomes zero. It increases as there is more overlap between the incomes of individuals belonging to different groups. The overlap terms move together with the narrowly defined within-inequality, and we shall treat them together.</p>
<p class="P4">We can write the Gini decomposition across recipients belonging to groups <em>i</em> (1, 2, . . . <em>r</em>) as</p>
</a><p class="eq"><a id="page_24"></a><a id="id_977">(1.1)      <img alt="" class="svg1" src="html/images/EQ001.svg"/></a></p><a id="id_977">
</a><p class="P51"><a id="id_977">where μ = overall mean income, <em>ȳi</em> = mean income of <em>i</em>-th group, <em>p<sub>i</sub></em> = population share of <em>i</em>-th group, <em>s<sub>i</sub></em> = share of <em>i</em>-th group in total income, <em>G<sub>i</sub></em> = Gini of <em>i</em>-th group, and <em>L</em> = the overlap term. The first term in </a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_977">equation (1.1)</a> is the between-group inequality; the second term, the narrowly defined within-group inequality; the third, the overlap term. The second and third terms are in the further text considered as “within-group inequality.”</p>
<p class="P4">We can now see that higher overall US labor income Gini (<em>G</em>) may be the result of greater group Ginis (<em>G<sub>i</sub></em>), or greater share (<em>s<sub>i</sub></em>) of groups that have higher inequality of earnings, or finally, may be due to large mean income gaps between the groups (that is, to the between-component).</p>
<p class="C754">1.2.1 Disaggregating into Household Types, Based on the Number and Gender of Earners</p>
<p class="P51">In all countries, we first divide the population into six main groups, based on the number and the gender of the earners in these households: households that contain (1) one female earner, (2) one male earner, (3) one male and one female earner, (4) two female earners, (5) two male earners and, <a id="page_25">finally, (6) three or more earners. Later in the chapter, groups (1), (2), and (3) will be further subdivided into demographic groups, based on partnership and parenting status. Throughout this chapter, results are presented at the person level—albeit drawing on their household characteristics. When we refer to various household types, either their prevalence or their outcomes, we are reporting results about the persons who live in those household types.</a></p><a id="page_25">
</a><div class="C1136"><a id="page_25">
</a><p class="C1135"><a id="page_25"></a><a id="id_850"><img alt="" class="Image350" src="html/images/39_1.png"/></a></p><a id="id_850">
<p class="C743"><strong>Fig. 1.2 Typology of households based on number and gender of earners, further disaggregated by demographic groups based on partnership and parenting status</strong></p>
<p class="C1138"><em>Note</em>: The six main types of households are indicated by numbers 1–6.</p>
</a></div><a id="id_850">
</a><p class="P4"><a id="id_850"></a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_850">Figure 1.2</a> summarizes our typology of households. Earners are defined as people who report having received nonzero labor income during the survey’s reference period. <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#page_26">Table 1.1</a> reports the composition of the working-age population, across the six household types, in these study countries.<sup><span href="#/html/38_Notes#id_24" id="id_23" title="Link target outside of Sample">7</span></sup></p>
<p class="P4">As can be expected, three household types dominate to the extent that they include more than 80 percent of all persons in all countries—except for Hungary, Ireland, and Russia.<sup><span href="#/html/38_Notes#id_22" id="id_21" title="Link target outside of Sample">8</span></sup> The three dominant groups are: the “traditional”<sup><span href="#/html/38_Notes#id_20" id="id_19" title="Link target outside of Sample">9</span></sup> two-earner households composed of one female and one male earner (with a cross-country average share of 46 percent), one-male-earner households with an average share of 21 percent, and households with three or more earners, with 16 percent. The other three groups are less prevalent, although households with only one female earner (cross-country average share of 12 percent) do play, as we shall see below, an important role.</p>
<div class="C1136">
<p class="C1252"><strong><a id="page_26">Table 1.1 Composition of working-age population, across six main household types (where household types are based on the number and gender of earners)</a></strong></p><a id="page_26">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style35"><td class="Col_Style196">Country/group</td>
<td class="Col_Style264">1 female<br/>earner 1</td>
<td class="Col_Style264">1 male<br/>earner 2</td>
<td class="Col_Style264">1 male,<br/>1 female<br/>earner 3</td>
<td class="Col_Style264">2 female<br/>earners 4</td>
<td class="Col_Style264">2 male<br/>earners 5</td>
<td class="Col_Style264">3+<br/>earners 6</td>
<td class="Col_Style264">Sum of columns 2 + 3 + 6 7</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Australia</td>
<td class="Col_Style12">9.2</td>
<td class="Col_Style126">21.6</td>
<td class="Col_Style126">39.7</td>
<td class="Col_Style126">2.3</td>
<td class="Col_Style126">3.6</td>
<td class="Col_Style126">22.7</td>
<td class="Col_Style126">83.9</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Canada</td>
<td class="Col_Style12">9.5</td>
<td class="Col_Style126">14.7</td>
<td class="Col_Style126">43.5</td>
<td class="Col_Style126">2.5</td>
<td class="Col_Style126">3.2</td>
<td class="Col_Style126">25.5</td>
<td class="Col_Style126">83.8</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Czech Republic</td>
<td class="Col_Style12">8.6</td>
<td class="Col_Style126">23.3</td>
<td class="Col_Style126">47.7</td>
<td class="Col_Style126">1.5</td>
<td class="Col_Style126">2.2</td>
<td class="Col_Style126">16.8</td>
<td class="Col_Style126">87.7</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Denmark</td>
<td class="Col_Style126">11.8</td>
<td class="Col_Style126">13.4</td>
<td class="Col_Style126">47.8</td>
<td class="Col_Style126">2.1</td>
<td class="Col_Style126">2.1</td>
<td class="Col_Style126">22.1</td>
<td class="Col_Style126">83.3</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Estonia</td>
<td class="Col_Style126">16.0</td>
<td class="Col_Style126">20.3</td>
<td class="Col_Style126">47.4</td>
<td class="Col_Style126">2.7</td>
<td class="Col_Style126">1.3</td>
<td class="Col_Style126">12.3</td>
<td class="Col_Style126">79.9</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Finland</td>
<td class="Col_Style126">12.0</td>
<td class="Col_Style126">15.5</td>
<td class="Col_Style126">53.1</td>
<td class="Col_Style126">1.4</td>
<td class="Col_Style126">0.7</td>
<td class="Col_Style126">17.2</td>
<td class="Col_Style126">85.8</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">France</td>
<td class="Col_Style126">14.7</td>
<td class="Col_Style126">19.7</td>
<td class="Col_Style126">55.8</td>
<td class="Col_Style126">1.1</td>
<td class="Col_Style126">1.4</td>
<td class="Col_Style12">6.8</td>
<td class="Col_Style126">82.3</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Germany</td>
<td class="Col_Style126">14.0</td>
<td class="Col_Style126">19.7</td>
<td class="Col_Style126">48.6</td>
<td class="Col_Style126">1.0</td>
<td class="Col_Style126">1.7</td>
<td class="Col_Style126">15.0</td>
<td class="Col_Style126">83.3</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Greece</td>
<td class="Col_Style12">8.2</td>
<td class="Col_Style126">30.9</td>
<td class="Col_Style126">48.6</td>
<td class="Col_Style126">0.9</td>
<td class="Col_Style126">2.3</td>
<td class="Col_Style12">7.3</td>
<td class="Col_Style126">86.8</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Hungary</td>
<td class="Col_Style126">17.6</td>
<td class="Col_Style126">24.7</td>
<td class="Col_Style126">39.6</td>
<td class="Col_Style126">1.6</td>
<td class="Col_Style126">0.7</td>
<td class="Col_Style12">9.1</td>
<td class="Col_Style126">73.4</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Iceland</td>
<td class="Col_Style126">10.1</td>
<td class="Col_Style126">11.1</td>
<td class="Col_Style126">45.3</td>
<td class="Col_Style126">2.1</td>
<td class="Col_Style126">1.0</td>
<td class="Col_Style126">30.4</td>
<td class="Col_Style126">86.8</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Ireland</td>
<td class="Col_Style126">18.2</td>
<td class="Col_Style126">23.6</td>
<td class="Col_Style126">41.1</td>
<td class="Col_Style126">2.2</td>
<td class="Col_Style126">3.9</td>
<td class="Col_Style126">11.0</td>
<td class="Col_Style126">75.7</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Israel</td>
<td class="Col_Style126">10.7</td>
<td class="Col_Style126">24.1</td>
<td class="Col_Style126">40.8</td>
<td class="Col_Style126">1.9</td>
<td class="Col_Style126">3.1</td>
<td class="Col_Style126">19.2</td>
<td class="Col_Style126">84.1</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Italy</td>
<td class="Col_Style126">10.1</td>
<td class="Col_Style126">34.0</td>
<td class="Col_Style126">44.8</td>
<td class="Col_Style126">0.8</td>
<td class="Col_Style126">4.0</td>
<td class="Col_Style12">6.3</td>
<td class="Col_Style126">85.1</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Luxembourg</td>
<td class="Col_Style126">10.7</td>
<td class="Col_Style126">25.0</td>
<td class="Col_Style126">51.5</td>
<td class="Col_Style126">0.7</td>
<td class="Col_Style126">2.3</td>
<td class="Col_Style12">9.7</td>
<td class="Col_Style126">86.2</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Netherlands</td>
<td class="Col_Style12">9.3</td>
<td class="Col_Style126">15.6</td>
<td class="Col_Style126">51.7</td>
<td class="Col_Style126">1.3</td>
<td class="Col_Style126">2.2</td>
<td class="Col_Style126">18.8</td>
<td class="Col_Style126">86.1</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Norway</td>
<td class="Col_Style126">12.0</td>
<td class="Col_Style126">15.0</td>
<td class="Col_Style126">48.3</td>
<td class="Col_Style126">1.4</td>
<td class="Col_Style126">1.5</td>
<td class="Col_Style126">20.2</td>
<td class="Col_Style126">83.5</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Poland</td>
<td class="Col_Style126">14.0</td>
<td class="Col_Style126">28.7</td>
<td class="Col_Style126">42.3</td>
<td class="Col_Style126">1.5</td>
<td class="Col_Style126">3.3</td>
<td class="Col_Style126">10.2</td>
<td class="Col_Style126">81.2</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Russia</td>
<td class="Col_Style126">16.9</td>
<td class="Col_Style126">17.3</td>
<td class="Col_Style126">39.6</td>
<td class="Col_Style126">2.9</td>
<td class="Col_Style126">2.6</td>
<td class="Col_Style126">20.7</td>
<td class="Col_Style126">77.6</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Slovakia</td>
<td class="Col_Style12">8.3</td>
<td class="Col_Style126">14.4</td>
<td class="Col_Style126">43.4</td>
<td class="Col_Style126">1.4</td>
<td class="Col_Style126">1.9</td>
<td class="Col_Style126">30.5</td>
<td class="Col_Style126">88.3</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Slovenia</td>
<td class="Col_Style12">9.3</td>
<td class="Col_Style126">15.8</td>
<td class="Col_Style126">50.6</td>
<td class="Col_Style126">1.4</td>
<td class="Col_Style126">1.9</td>
<td class="Col_Style126">21.1</td>
<td class="Col_Style126">87.4</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">Spain</td>
<td class="Col_Style126">10.8</td>
<td class="Col_Style126">25.7</td>
<td class="Col_Style126">46.6</td>
<td class="Col_Style126">1.5</td>
<td class="Col_Style126">2.9</td>
<td class="Col_Style126">10.0</td>
<td class="Col_Style126">82.3</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">United Kingdom</td>
<td class="Col_Style126">13.2</td>
<td class="Col_Style126">21.2</td>
<td class="Col_Style126">46.6</td>
<td class="Col_Style126">1.8</td>
<td class="Col_Style126">2.2</td>
<td class="Col_Style126">14.7</td>
<td class="Col_Style126">82.5</td>
</tr>
<tr class="Row_Style185"><td class="Col_Style46">United States</td>
<td class="Col_Style126">14.8</td>
<td class="Col_Style126">22.1</td>
<td class="Col_Style126">42.2</td>
<td class="Col_Style126">2.3</td>
<td class="Col_Style126">3.0</td>
<td class="Col_Style126">15.3</td>
<td class="Col_Style126">79.6</td>
</tr>
<tr class="Row_Style140"><td class="Col_Style46"><em>Unweighted means</em></td>
<td class="Col_Style126"><em>12.1</em></td>
<td class="Col_Style126"><em>20.7</em></td>
<td class="Col_Style126"><em>46.1</em></td>
<td class="Col_Style126"><em>1.7</em></td>
<td class="Col_Style126"><em>2.3</em></td>
<td class="Col_Style126"><em>16.4</em></td>
<td class="Col_Style126"><em>83.2</em></td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/40_1.png"/></p>
</a></div><a id="page_26">
</a><p class="P4"><a id="page_26">In </a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_851">figure 1.3</a>, we take a first look at US labor income inequality within each of these household types in comparative context. For each type, the figure indicates the position of the US Gini (in black) compared to the other 23 countries. For three household types (one-male-earner, one male and one female earner, and two male earners), the US has the most unequal distribution of all countries; for the other three household types, the US distribution is the second most unequal.<sup><span href="#/html/38_Notes#id_26" id="id_25" title="Link target outside of Sample">10</span></sup> In no case, is the US Gini even close to the median Gini for a given household type, much less lower than it.</p>
<p class="P4">Therefore, breaking the overall labor earnings distribution into household <a id="page_27">types reinforces our previous finding: US labor income is very unequally distributed, not only in the aggregate, but <em>within</em> each household type.</a></p><a id="page_27">
</a><div class="C1136"><a id="page_27">
</a><p class="C1135"><a id="page_27"></a><a id="id_851"><img alt="" class="Image350" src="html/images/41_1.png"/></a></p><a id="id_851">
<p class="C743"><strong>Fig. 1.3 Inequality in six main household types (where household types are based on the number and gender of earners)</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows the Gini of a given group and country. The US Gini is black. Ginis are ordered from the highest to the lowest.</p>
</a></div><a id="id_851">
</a><div class="C1136"><a id="id_851">
</a><p class="C1135"><a id="id_851"></a><a id="id_852"><img alt="" class="Image350" src="html/images/41_2.png"/></a></p><a id="id_852">
<p class="C743"><strong>Fig. 1.4 Relative income of six main household types</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows mean income of a group compared to the mean income of the country. The US values are black. Values are ordered from the highest to the lowest.</p>
</a></div><a id="id_852">
</a><p class="P4"><a id="id_852">We need to also look at <em>between</em>-group inequality (that is, between the six household types). Consider now </a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_852">figure 1.4</a>, which is constructed similarly to <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_851">figure 1.3</a> but where we look at relative earning levels of household types. For <a id="page_28">example, the one-female-earner households’ mean earnings<sup></sup></a><span href="#/html/38_Notes#id_30" id="id_29" title="Link target outside of Sample">11</span> are in relative terms the lowest in Israel (only 45 percent of the country mean) and the highest in Hungary (75 percent of the country mean). The US at 54 percent is somewhat below the median for OECD countries included here. A look at <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_852">figure 1.4</a> shows that the US position, with the exception of the two-female-earner households (relatively low) and the one-male-earner households (relatively high) is not exceptional. In other words, when it comes to the relative earnings of various demographic groups, the US is far from being a cross-national outlier: groups’ relative earning levels track closely other high-income countries’ relative earnings levels. This, in turn, implies that the origin of high labor income inequality in the US is not to be found in unusually high earnings of some demographic groups and unusually low earnings of others, but in systematically high earnings inequalities <em>within</em> each individual household type.</p>
<p class="P4">We confirm this conclusion by looking at the results of the decomposition exercise using <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_977">equation (1.1)</a>. Each country’s overall inequality is broken into between- and within-inequalities (vis-à-vis the six groups). The US within-inequality Gini (shown in <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#page_29">table 1.2</a>, column 3) is 0.311. This means that if the mean earnings of the six household types were exactly equal, the overall labor income inequality would be 0.311, which is by far the highest value among the countries considered here. Canada and Luxembourg have the second highest within-inequality, with a Gini of 0.282, some 10 percent lower than the US. When we look at the between-inequality, however, the US is far from exceptional. Although the within-inequality of the US is 34 percent higher than the mean of the other 23 countries, the between-inequality is practically the same as the mean for other countries.</p>
<p class="P4">Finally, we can assess this from another vantage point by using the Theil index instead of the Gini. The advantage of the Theil, in this particular case, is that it is exactly decomposable between different components.</p>
<p class="P4"><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_817">Table 1.3</a> reports the results of two Theil decompositions for the US case. The first column presents the Theil T—or the GE(1)—where the weights are income shares. The second column presents the Theil L, or the GE(0)—the mean log deviation—where the weights are population shares.</p>
<p class="P4">When we assume that the US has both the same demographic structure and the same relative group incomes as the average of the other 23 OECD countries, the Theil index, in its two variants, is reduced by either 3 or 6 percent. The changes seem minimal and reinforce our view that the dominant factor explaining high market income inequality in the US is high inequality within each demographic group.<sup><span href="#/html/38_Notes#id_28" id="id_27" title="Link target outside of Sample">12</span></sup></p>
<div class="C1136">
<p class="C1252"><strong><a id="page_29">Table 1.2 Decomposition: Between-group and within-group components (for six household types)</a></strong></p><a id="page_29">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style13"><td class="Col_Style196"></td>
<td class="Col_Style264">Overall<br/>labor Gini<br/>(1)</td>
<td class="Col_Style264">Between Gini<br/>component<br/>(2)</td>
<td class="Col_Style264">Within Gini<br/>component<br/>(3)</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Australia</td>
<td class="Col_Style126">0.357</td>
<td class="Col_Style126">0.119</td>
<td class="Col_Style126">0.238</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Canada</td>
<td class="Col_Style126">0.394</td>
<td class="Col_Style126">0.112</td>
<td class="Col_Style126">0.282</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Czech Republic</td>
<td class="Col_Style126">0.323</td>
<td class="Col_Style126">0.129</td>
<td class="Col_Style126">0.193</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Denmark</td>
<td class="Col_Style126">0.323</td>
<td class="Col_Style126">0.112</td>
<td class="Col_Style126">0.211</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Estonia</td>
<td class="Col_Style126">0.368</td>
<td class="Col_Style126">0.124</td>
<td class="Col_Style126">0.245</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Finland</td>
<td class="Col_Style126">0.335</td>
<td class="Col_Style126">0.103</td>
<td class="Col_Style126">0.232</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">France</td>
<td class="Col_Style126">0.365</td>
<td class="Col_Style126">0.114</td>
<td class="Col_Style126">0.251</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Germany</td>
<td class="Col_Style126">0.363</td>
<td class="Col_Style126">0.109</td>
<td class="Col_Style126">0.254</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Greece</td>
<td class="Col_Style126">0.365</td>
<td class="Col_Style126">0.127</td>
<td class="Col_Style126">0.238</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Hungary</td>
<td class="Col_Style126">0.394</td>
<td class="Col_Style126">0.149</td>
<td class="Col_Style126">0.245</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Iceland</td>
<td class="Col_Style126">0.330</td>
<td class="Col_Style126">0.127</td>
<td class="Col_Style126">0.202</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Ireland</td>
<td class="Col_Style126">0.430</td>
<td class="Col_Style126">0.186</td>
<td class="Col_Style126">0.243</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Israel</td>
<td class="Col_Style126">0.442</td>
<td class="Col_Style126">0.184</td>
<td class="Col_Style126">0.258</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Italy</td>
<td class="Col_Style126">0.320</td>
<td class="Col_Style126">0.149</td>
<td class="Col_Style126">0.171</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Luxembourg</td>
<td class="Col_Style126">0.366</td>
<td class="Col_Style126">0.084</td>
<td class="Col_Style126">0.282</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Netherlands</td>
<td class="Col_Style126">0.336</td>
<td class="Col_Style126">0.100</td>
<td class="Col_Style126">0.236</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Norway</td>
<td class="Col_Style126">0.337</td>
<td class="Col_Style126">0.119</td>
<td class="Col_Style126">0.218</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Poland</td>
<td class="Col_Style126">0.358</td>
<td class="Col_Style126">0.135</td>
<td class="Col_Style126">0.223</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Russia</td>
<td class="Col_Style126">0.368</td>
<td class="Col_Style126">0.156</td>
<td class="Col_Style126">0.212</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Slovakia</td>
<td class="Col_Style126">0.311</td>
<td class="Col_Style126">0.136</td>
<td class="Col_Style126">0.175</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Slovenia</td>
<td class="Col_Style126">0.277</td>
<td class="Col_Style126">0.128</td>
<td class="Col_Style126">0.149</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">Spain</td>
<td class="Col_Style126">0.366</td>
<td class="Col_Style126">0.136</td>
<td class="Col_Style126">0.230</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46">United Kingdom</td>
<td class="Col_Style126">0.400</td>
<td class="Col_Style126">0.124</td>
<td class="Col_Style126">0.277</td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46"><strong>United States</strong></td>
<td class="Col_Style126"><strong>0.436</strong></td>
<td class="Col_Style126"><strong>0.125</strong></td>
<td class="Col_Style126"><strong>0.311</strong></td>
</tr>
<tr class="Row_Style127"><td class="Col_Style46"><em>Non-US mean</em></td>
<td class="Col_Style126"><em>0.358</em></td>
<td class="Col_Style126"><em>0.129</em></td>
<td class="Col_Style126"><em>0.229</em></td>
</tr>
<tr class="Row_Style58"><td class="Col_Style46"><strong><em>US/non-US mean</em></strong></td>
<td class="Col_Style126"><strong><em>1.21</em></strong></td>
<td class="Col_Style126"><strong><em>0.97</em></strong></td>
<td class="Col_Style12"><strong><em>1.34</em></strong></td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/43_1.png"/></p>
<p class="C1138"><em>Note</em>: Within-inequality includes the narrowly defined within-inequality and the overlap component; see equation (1.1).</p>
</a></div><a id="page_29">
</a><div class="C1136"><a id="page_29">
</a><p class="C1252"><a id="page_29"><strong></strong></a><strong><a id="id_817">Table 1.3 Theil counterfactual: US inequality with OECD average demographic structure and relative mean group incomes</a></strong></p><a id="id_817">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style143"><td class="Col_Style196"></td>
<td class="Col_Style264">Theil T—GE(1)</td>
<td class="Col_Style264">Theil L—GE(0)</td>
</tr>
<tr class="Row_Style165"><td class="Col_Style46">Actual US inequality</td>
<td class="Col_Style126">0.342</td>
<td class="Col_Style126">0.380</td>
</tr>
<tr class="Row_Style165"><td class="Col_Style46">US inequality if demographic structure were as OECD average (change)</td>
<td class="Col_Style126">0.364 (+6%)</td>
<td class="Col_Style126">0.334 (−12%)</td>
</tr>
<tr class="Row_Style165"><td class="Col_Style46">US inequality if relative group incomes were as OECD average (change)</td>
<td class="Col_Style126">0.312 (−9%)</td>
<td class="Col_Style126">0.334 (+6%)</td>
</tr>
<tr class="Row_Style113"><td class="Col_Style46"><em>US inequality if both demographic structure and relative group incomes were as OECD average (change)</em></td>
<td class="Col_Style126"><em>0.333 (</em>−<em>3%)</em></td>
<td class="Col_Style126"><em>0.360 (</em>−<em>6%)</em></td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/43_2.png"/></p>
</a></div><a id="id_817">
</a><p class="P4"><a id="id_817"></a><a id="page_30">We have thus established that US labor income inequality is, together with Israel’s, the highest among all of the OECD countries included here and that the source of that inequality is not to be found in vastly different mean labor incomes across different household types, but in the consistently higher inequality with which labor incomes are distributed within each household type. We now continue by looking in greater detail into three prevalent household types: one-female-earner households, one-male-earner households, and two-earner “traditional” households (which contain one female and one male earner).</a></p><a id="page_30">
<p class="C826"><strong>1.3 Earnings Inequality within One-Earner and “Traditional” Households: Further Disaggregation by Partnership and Parenting Status</strong></p>
<p class="C846">1.3.1 One-Female-Earner Households</p>
<p class="P51">We begin by looking at households that contain only one earner—one who is female. The prevalence of these households across the countries included here is very uneven: at the low end are Greece, Slovakia, and the Czech Republic where fewer than 9 percent of households contain only one earner, who is female. At the other end are Estonia and (as mentioned earlier) Hungary, Ireland, and Russia, which each contain more than 16 percent of households of this type. The US falls in the upper range, with the share of one-female-earner households being 15 percent.</p>
</a><p class="P4"><a id="page_30">In our next analysis, we divide one-female-earner households into five demographic subgroups, corresponding to the households in which they live: couple-headed households with one or more children, couple-headed households without children, single-headed<sup></sup></a><span href="#/html/38_Notes#id_34" id="id_33" title="Link target outside of Sample">13</span> households with children, single-headed household without children, and others.<sup><span href="#/html/38_Notes#id_32" id="id_31" title="Link target outside of Sample">14</span></sup> As we did before for all households, here we look first at inequality levels within each household type and then at the relative incomes of each type. The most common type among one-female-earner households in the US, and across these 24 countries, is a household headed by a single woman with children. The next most prevalent types are couple-headed households with children (where, by definition, a female is the only earner) and single-female-headed households <a id="page_31">without children. In the US, these three household types comprise over 80 percent of one-female-earner households.</a></p><a id="page_31">
</a><div class="C1136"><a id="page_31">
</a><p class="C1135"><a id="page_31"></a><a id="id_808"><img alt="" class="Image349" src="html/images/45_1.png"/></a></p><a id="id_808">
<p class="C743"><strong>Fig. 1.5. Inequality of five subgroups among one-female-earner households</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows the Gini of a given group and country. The US Ginis are black. Ginis are ordered from the highest to the lowest.</p>
</a></div><a id="id_808">
</a><p class="P4"><a id="id_808">But is the distribution of labor income in such American households more unequal than in the other countries? </a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_808">Figure 1.5</a>, with the same interpretation as <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_851">figure 1.3</a>, provides an answer. In all cases, US inequality is greater than the median inequality among 24 countries, and is always ranked either the fourth or the fifth from the top. Particularly interesting is the situation of single-headed one-female-earner households with children, where the US Gini is (a high) 0.48 while the mean Gini for this type of household, is 0.40.</p>
<p class="P4">Very high inequality among single-headed one-female-earner households, both with and without children, in the US clearly implies that they are economically and socially diverse. We shall find similar high heterogeneity among single one-male-earner households without children.</p>
<p class="P4">Next, we look at relative incomes (see <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#page_32">figure 1.6</a>). The situation here is familiar: US subgroup mean relative incomes are not dissimilar to the median relative incomes across the 24 countries. The differences are minimal (e.g., for a couple with a child, the average labor income is 41 percent of US overall mean vs. 45 percent across the 24 countries). The exception is the low income level of one-female-earner households with children (that is, single mothers): their relative income in the US is 40 percent of the overall mean while the countries’ average is 50 percent. An ethnic/racial component may be important here, as we find (not reported here) that these households, when headed by Hispanics and African Americans, have mean labor incomes that are only about 30 percent of the overall US mean.</p>
<div class="C1136">
<p class="C1135"><a id="page_32"><img alt="" class="Image350" src="html/images/46_1.png"/></a></p><a id="page_32">
<p class="C743"><strong>Fig. 1.6 Relative income of five subgroups among one-female-earner households</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows mean income of a subgroup compared to the mean income of the country. The US values are black. Values are ordered from the highest to the lowest.</p>
</a></div><a id="page_32">
<p class="C754">1.3.2 One-Male-Earner Households</p>
</a><p class="P51"><a id="page_32">We now move to one-male-earner households, where we keep the same household classification as for one-female-earner households. The prevalence of these households varies markedly across countries. At the low end, in Iceland, Denmark, Canada, and Slovakia, their share is less than 15 percent. But at the high end, Italy and Greece—with comparatively low levels of female employment—have more than 30 percent of one-male-earner households. The US result (22 percent) falls near the cross-national mean (21 percent).<sup></sup></a><span href="#/html/38_Notes#id_36" id="id_35" title="Link target outside of Sample">15</span></p>
<p class="P4">The results for inequality are familiar (see <a href="#/html/#page_33">figure 1.7</a>): US households have a much greater labor income inequality than the rest of the countries, and for two groups in particular (couple-headed households with and without children) US inequality is the highest of all. But it is among the highest in the other three types of one-male-earner households as well.</p>
<p class="P4"><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_853">Figure 1.8</a> shows the results for the relative income of single one-male-earner households. In three out of five types here, US relative mean income is around the cross-country median. The exceptions are one-male-earner households (couples with or without children) whose relative income is among the highest. These two groups are interesting because they display unusually high relative mean incomes with similarly unusually high inequality.</p>
<div class="C1136">
<p class="C1135"><a id="page_33"><img alt="" class="Image350" src="html/images/47_1.png"/></a></p><a id="page_33">
<p class="C743"><strong>Fig. 1.7 Inequality of five subgroups among one-male-earner households</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows the Gini of a given group and country. The US Ginis are black. Ginis are ordered from the highest to the lowest.</p>
</a></div><a id="page_33">
</a><div class="C1136"><a id="page_33">
</a><p class="C1135"><a id="page_33"></a><a id="id_853"><img alt="" class="Image349" src="html/images/47_2.png"/></a></p><a id="id_853">
<p class="C743"><strong>Fig. 1.8 Relative income of five subgroups among one-male-earner households</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows mean income of a group compared to the mean income of the country. The US values are black. Values are ordered from the highest to the lowest.</p>
</a></div><a id="id_853">
<p class="C754">1.3.3 “Traditional” Households</p>
</a><p class="P51"><a id="id_853">“Traditional” (one male earner and one female earner) households comprise the largest share of all households, from just under 40 percent in Australia, Hungary, and Russia to 56 percent in France. (The US with 42 percent is on the low side, modestly below the unweighted mean of 46 percent). </a><a id="page_34">Here, we look at only two subgroups: “traditional” households with and without children.</a></p><a id="page_34">
</a><div class="C1136"><a id="page_34">
</a><p class="C1135"><a id="page_34"></a><a id="id_809"><img alt="" class="Image349" src="html/images/48_1.png"/></a></p><a id="id_809">
<p class="C743"><strong>Fig. 1.9 Inequality of two subgroups of “traditional” households</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows the Gini of a given group and country. The US Ginis are black. Ginis are ordered from the highest to the lowest.</p>
</a></div><a id="id_809">
</a><p class="P4"><a id="id_809">US inequality is again very high (see </a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_809">figure 1.9</a>). US inequality is the highest of all countries, among these two-earner couples with children—with a Gini of 0.37 compared to the cross-country median Gini of just less than 0.30. US inequality is second highest, among two-earner couples without children.</p>
<p class="P4">When it comes to relative incomes (see <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#id_810">figure 1.10</a>), US relative labor income for two-earner households with children is very close to the median for the 24 countries; it is higher than the cross-country median, however, for two-earner couples without children.</p>
<p class="C754">1.3.4 Regression Analysis</p>
<p class="P51">To tease out the specificity of US inequality, we estimate regressions where the Gini coefficient for each country/group is regressed on groups’ relative mean income (i.e., relative to the mean of that country) and dummy variables for the subgroups (<em>N</em> = 15) and countries (<em>N</em> = 24). The omitted household type is one-male-one-female-earner with children and the omitted country is Denmark (with low inequality).</p>
<p class="P4">We use two specifications of the regression: an unweighted one, and a weighted regression where each group is weighted by its share in the population of a given country. The latter adjusts for variation in household compositions across countries. We are, of course, interested in the coefficient on the dummy variable for the US. The results are reported in <a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#page_36">table 1.4</a>.</p>
<p class="P4">Compared to the omitted country (Denmark), the coefficient on the US dummy is 0.069 in the unweighted formulation, and 0.101 in the weighted formulation. It is statistically significant at less than 0.01 in both cases. This means that, on average (whatever demographic group we take), US inequality is between 6.9 and 10.1 Gini points greater than Denmark’s. Perhaps more revealing is the fact that in both formulations, the US coefficient is the largest of all country dummies. The next largest positive coefficient in the unweighted formulation is Canada’s (5.4 Gini points more unequal than Denmark) and, in the weighted formulation, Israel’s (8.2 Gini points more unequal than Denmark). So, in terms of within-group inequalities, the US is, on average, more unequal than the second most unequal OECD country by between 1.5 and 1.9 Gini points.</p>
<div class="C1136">
<p class="C1135"><a id="page_35"></a><a id="id_810"><img alt="" class="Image349" src="html/images/49_1.png"/></a></p><a id="id_810">
<p class="C743"><strong>Fig. 1.10 Relative income of two subgroups of “traditional” households</strong></p>
<p class="C1138"><em>Notes</em>: Each bar shows mean income of a group compared to the mean income of the country. The US values are black. Values are ordered from the highest to the lowest.</p>
</a></div><a id="id_810">
<p class="C754">1.3.5 Robustness of the Results</p>
<p class="P51">There are two possible limitations of our results that need to be addressed. The first refers to the composition of the population (i.e., shares of different demographic groups); the second to the year of study (2010) selected here.</p>
<p class="C813">Consider group composition first. Earlier in this chapter, we noted that the higher overall labor income Gini in the US, compared to other relatively similar countries, could be the result of</p>
<p class="C862">(1) greater group Ginis (the “within” component);</p>
<p class="C862">(2) larger mean income gaps between the groups (the “between” component); and/or</p>
<p class="C863">(3) greater shares of groups that have higher level of inequality.</p>
<p class="P4">Throughout this chapter, we formally assessed the contributions of the first two of these three factors—the “within” and “between” components of inequality—but we did not present a detailed look at the third. The regression analysis, however, shows that the introduction of the demographic composition does not affect the results; it rather makes them stronger because the US dummy variable in the population-weighted regression is greater than in the unweighted formulation. So, if anything, the US has a “favorable” demographic composition.</p>
</a><div class="C1136"><a id="id_810">
</a><p class="C1252"><a id="id_810"><strong></strong></a><strong><a id="page_36">Table 1.4 US income inequality exceptionalism (dependent variable: Gini coefficient of household type/country)</a></strong></p><a id="page_36">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style126"><td class="Col_Style46"></td>
<td class="Col_Style46"></td>
<td class="Col_Style63" colspan="2">Coefficient</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style46"></td>
<td class="Col_Style63" colspan="2">(<em>p</em>-value)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style46"></td>
<td class="Col_Style63" colspan="2">* = significance &lt; 0.05</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style46"></td>
<td class="Col_Style251" colspan="2">** = significance &lt; 0.01</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254">Variable</td>
<td class="Col_Style254"></td>
<td class="Col_Style7">Unweighted regression</td>
<td class="Col_Style31">Population-share weighted regression</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">Relative group mean</td>
<td class="Col_Style46"></td>
<td class="Col_Style199">−0.036</td>
<td class="Col_Style134">−0.003</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style254"></td>
<td class="Col_Style7">(0.20)</td>
<td class="Col_Style31">(0.89)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">Three or more earners</td>
<td class="Col_Style46"></td>
<td class="Col_Style199">−0.028</td>
<td class="Col_Style134">−0.034**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style254"></td>
<td class="Col_Style7">(0.09)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">Two earners</td>
<td class="Col_Style64">Female</td>
<td class="Col_Style199">0.022</td>
<td class="Col_Style134">0.035*</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.23)</td>
<td class="Col_Style31">(0.02)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Male</td>
<td class="Col_Style199">0.034*</td>
<td class="Col_Style134">0.033**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.04)</td>
<td class="Col_Style31">(0.01)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">One female earner</td>
<td class="Col_Style64">Couple with children</td>
<td class="Col_Style199">0.099**</td>
<td class="Col_Style134">0.136**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Couple without children</td>
<td class="Col_Style199">0.048*</td>
<td class="Col_Style134">0.065**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.03)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Other</td>
<td class="Col_Style199">0.057*</td>
<td class="Col_Style134">0.081**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.03)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Single with children</td>
<td class="Col_Style199">0.082**</td>
<td class="Col_Style134">0.098**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Single without children</td>
<td class="Col_Style199">0.066**</td>
<td class="Col_Style134">0.078**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">One male earner</td>
<td class="Col_Style64">Couple with children</td>
<td class="Col_Style199">0.089**</td>
<td class="Col_Style134">0.097**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Couple without children</td>
<td class="Col_Style199">0.054**</td>
<td class="Col_Style134">0.067**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Other</td>
<td class="Col_Style199">0.049*</td>
<td class="Col_Style134">0.074**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.05)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Single with children</td>
<td class="Col_Style199">0.086**</td>
<td class="Col_Style134">0.117**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46"></td>
<td class="Col_Style64">Single without children</td>
<td class="Col_Style199">0.087**</td>
<td class="Col_Style134">0.094**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">One male one female earner</td>
<td class="Col_Style64">Couple without children</td>
<td class="Col_Style199">0.104</td>
<td class="Col_Style134">0.002</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.54)</td>
<td class="Col_Style31">(0.80)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">US dummy</td>
<td class="Col_Style64"></td>
<td class="Col_Style199">0.069**</td>
<td class="Col_Style134">0.101**</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(0.00)</td>
<td class="Col_Style31">(0.00)</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style46">Adjusted R-squared (F)</td>
<td class="Col_Style64"></td>
<td class="Col_Style199">0.59</td>
<td class="Col_Style134">0.82</td>
</tr>
<tr class="Row_Style135"><td class="Col_Style254"></td>
<td class="Col_Style42"></td>
<td class="Col_Style7">(12.3)</td>
<td class="Col_Style31">(38.9)</td>
</tr>
<tr class="Row_Style30"><td class="Col_Style254">Number of observations</td>
<td class="Col_Style42"></td>
<td class="Col_Style7">360</td>
<td class="Col_Style31">360</td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/50_1.png"/></p>
<p class="C1138"><em>Note</em>: The regression is based on 360 observations, i.e., 24 countries × 15 subgroups. The omitted household type is one male, one female earner with children, and the omitted country is Denmark. Coefficients on dummy variables for countries other than the US are not shown.</p>
</a></div><a id="page_36">
</a><div class="C1136"><a id="page_36">
</a><p class="C1252"><a id="page_36"><strong></strong></a><strong><a id="page_37">Table 1.5 Population shares of household types</a></strong></p><a id="page_37">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style104"><td class="Col_Style196">Type of household</td>
<td class="Col_Style264">Share in the US (percent) (1)</td>
<td class="Col_Style264">Average share in other 23 countries (percent) (2)</td>
<td class="Col_Style264">Difference between US share and average share in other countries (percentage points) (3) = (1) − (2)</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style46">One female earner</td>
<td class="Col_Style126"></td>
<td class="Col_Style126"></td>
<td class="Col_Style126"></td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Couple with children</td>
<td class="Col_Style12">2.3</td>
<td class="Col_Style12">3.5</td>
<td class="Col_Style126">−1.2</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Couple without children</td>
<td class="Col_Style12">1.0</td>
<td class="Col_Style12">0.9</td>
<td class="Col_Style12">0.1</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Other</td>
<td class="Col_Style12">1.8</td>
<td class="Col_Style12">0.6</td>
<td class="Col_Style12">1.1</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Single with children</td>
<td class="Col_Style12">6.5</td>
<td class="Col_Style12">4.1</td>
<td class="Col_Style12">2.4</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Single without children</td>
<td class="Col_Style12">3.2</td>
<td class="Col_Style12">3.2</td>
<td class="Col_Style12">0.0</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style46">One male earner</td>
<td class="Col_Style126"></td>
<td class="Col_Style12"></td>
<td class="Col_Style12"></td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Couple with children</td>
<td class="Col_Style126">13.1</td>
<td class="Col_Style126">13.0</td>
<td class="Col_Style12">0.1</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Couple without children</td>
<td class="Col_Style12">2.2</td>
<td class="Col_Style12">1.7</td>
<td class="Col_Style12">0.5</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Other</td>
<td class="Col_Style12">1.5</td>
<td class="Col_Style12">0.8</td>
<td class="Col_Style12">0.7</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Single with children</td>
<td class="Col_Style12">1.2</td>
<td class="Col_Style12">1.0</td>
<td class="Col_Style12">0.2</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">Single without children</td>
<td class="Col_Style12">4.0</td>
<td class="Col_Style12">4.3</td>
<td class="Col_Style126">−0.3</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style46">“Traditional”</td>
<td class="Col_Style126"></td>
<td class="Col_Style114"></td>
<td class="Col_Style12"></td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">with children</td>
<td class="Col_Style126">29.6</td>
<td class="Col_Style126">32.9</td>
<td class="Col_Style126">−3.3</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style244">without children</td>
<td class="Col_Style126">12.6</td>
<td class="Col_Style126">13.4</td>
<td class="Col_Style126">−0.8</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style46">Two female earners</td>
<td class="Col_Style12">2.3</td>
<td class="Col_Style12">1.7</td>
<td class="Col_Style12">0.6</td>
</tr>
<tr class="Row_Style76"><td class="Col_Style46">Two male earners</td>
<td class="Col_Style12">3.0</td>
<td class="Col_Style12">2.3</td>
<td class="Col_Style12">0.7</td>
</tr>
<tr class="Row_Style28"><td class="Col_Style46">Three+ earners</td>
<td class="Col_Style126">15.3</td>
<td class="Col_Style126">16.4</td>
<td class="Col_Style126">−1.1</td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/51_1.png"/></p>
</a></div><a id="page_37">
</a><p class="P4"><a id="page_37">In </a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#page_37">table 1.5</a> we show the share of each subgroup in the US and the unweighted average shares of the same subgroups across the 23 comparator countries. The US shares diverge by more than 2 percentage points in only two cases. The first case is the one-male-one-female-earner couple with children: about 30 percent of the US population is living in such households versus 33 percent, on average, in the rest of these OECD countries. The second case is one-female-earner households where that earner is single with children; about 6.5 percent of the US population lives in that type of household but only 4 percent (on average) in the other OECD countries. (In common parlance, the US is slightly low on “traditional” households and slightly high vis-à-vis single mothers). In short and even leaving the regression results aside, we note that the US family composition is very similar to that of other countries. Thus, a unique compositional structure does not explain the high level of overall earnings inequality reported in the US.</p>
<p class="P4">Second, is the “story” that we report here stable over time, or is there something unusual about the year that we chose (2010)?</p>
<div class="C1136">
<p class="C1252"><strong><a id="page_38">Table 1.6 US inequality rankings among 24 OECD countries (1 = highest; 24 = lowest)</a></strong></p><a id="page_38">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style154"><td class="Col_Style46"></td>
<td class="Col_Style167"></td>
<td class="Col_Style264" colspan="2">Ranks</td>
<td class="Col_Style167"></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style196">Type of household</td>
<td class="Col_Style264">Subgroup share (US 2010)</td>
<td class="Col_Style264">1995</td>
<td class="Col_Style264">2010</td>
<td class="Col_Style264">Difference in US rank between two time points</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style46">One female earner</td>
<td class="Col_Style12"></td>
<td class="Col_Style126"></td>
<td class="Col_Style126"></td>
<td class="Col_Style126"></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Couple with children</td>
<td class="Col_Style12">2.3</td>
<td class="Col_Style126">2</td>
<td class="Col_Style126">5</td>
<td class="Col_Style126">−3</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Couple without children</td>
<td class="Col_Style12">1.0</td>
<td class="Col_Style126">6</td>
<td class="Col_Style126">4</td>
<td class="Col_Style12">2</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Other</td>
<td class="Col_Style12">1.8</td>
<td class="Col_Style126">2</td>
<td class="Col_Style126">6</td>
<td class="Col_Style126">−4</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244"><strong>Single with children</strong></td>
<td class="Col_Style12"><strong>6.5</strong></td>
<td class="Col_Style126"><strong>3</strong></td>
<td class="Col_Style126"><strong>3</strong></td>
<td class="Col_Style12"><strong>0</strong></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Single without children</td>
<td class="Col_Style12">3.2</td>
<td class="Col_Style126">1</td>
<td class="Col_Style126">3</td>
<td class="Col_Style126">−2</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style46">One male earner</td>
<td class="Col_Style12"></td>
<td class="Col_Style126"></td>
<td class="Col_Style126"></td>
<td class="Col_Style12"></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244"><strong>Couple with children</strong></td>
<td class="Col_Style126"><strong>13.1</strong></td>
<td class="Col_Style126"><strong>1</strong></td>
<td class="Col_Style126"><strong>1</strong></td>
<td class="Col_Style12"><strong>0</strong></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Couple without children</td>
<td class="Col_Style12">2.2</td>
<td class="Col_Style126">1</td>
<td class="Col_Style126">1</td>
<td class="Col_Style12">0</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Other</td>
<td class="Col_Style12">1.5</td>
<td class="Col_Style126">2</td>
<td class="Col_Style126">3</td>
<td class="Col_Style126">−1</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Single with children</td>
<td class="Col_Style12">1.2</td>
<td class="Col_Style126">5</td>
<td class="Col_Style126">8</td>
<td class="Col_Style126">−3</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244">Single without children</td>
<td class="Col_Style12">4.0</td>
<td class="Col_Style126">2</td>
<td class="Col_Style126">4</td>
<td class="Col_Style126">−2</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style46">“Traditional”</td>
<td class="Col_Style12"></td>
<td class="Col_Style126"></td>
<td class="Col_Style126"></td>
<td class="Col_Style12"></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244"><strong>with children</strong></td>
<td class="Col_Style126"><strong>29.6</strong></td>
<td class="Col_Style126"><strong>1</strong></td>
<td class="Col_Style126"><strong>1</strong></td>
<td class="Col_Style12"><strong>0</strong></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style244"><strong>without children</strong></td>
<td class="Col_Style126"><strong>12.6</strong></td>
<td class="Col_Style126"><strong>2</strong></td>
<td class="Col_Style126"><strong>2</strong></td>
<td class="Col_Style12"><strong>0</strong></td>
</tr>
<tr class="Row_Style47"><td class="Col_Style46">Two female earners</td>
<td class="Col_Style12">2.3</td>
<td class="Col_Style126">3</td>
<td class="Col_Style126">2</td>
<td class="Col_Style12">1</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style46">Two male earners</td>
<td class="Col_Style12">3.0</td>
<td class="Col_Style126">1</td>
<td class="Col_Style126">1</td>
<td class="Col_Style12">0</td>
</tr>
<tr class="Row_Style47"><td class="Col_Style46"><strong>Three+ earners</strong></td>
<td class="Col_Style126"><strong>15.3</strong></td>
<td class="Col_Style126"><strong>2</strong></td>
<td class="Col_Style126"><strong>2</strong></td>
<td class="Col_Style12"><strong>0</strong></td>
</tr>
<tr class="Row_Style168"><td class="Col_Style46"><em>Unweighted mean rank</em></td>
<td class="Col_Style12"></td>
<td class="Col_Style126"><em>2.3</em></td>
<td class="Col_Style126"><em>3.1</em></td>
<td class="Col_Style12"></td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/52_1.png"/></p>
<p class="P84"><em>Note</em>: The five rows in bold account for more than 75 percent of persons in the US.</p>
</a></div><a id="page_38">
</a><p class="P4"><a id="page_38"></a><a href="#/html/11_1__In_Search_of_the_Roots_of_Ame#page_38">Table 1.6</a> provides a window onto the answer to that question. It shows, for each subgroup, how US inequality (captured by the Gini) is ranked with respect to the 24 countries in our study at two points in time, 1995 (Wave IV)<sup><span href="#/html/38_Notes#id_38" id="id_37" title="Link target outside of Sample">16</span></sup> and 2010 (Wave VIII). This is not, of course, a huge sweep of time but it is the longest interval for which we had data on all 24 countries; and 13 years (including the onset of the global financial crisis) is not a trivial passage of time.</p>
<p class="P4">Consider the five most prevalent subgroups—shown in bold. These groups constitute over 75 percent of the US population. In each of these five subgroups, the US rank (within the 24 countries) is exactly the same at both time points. Across all 15 subgroups, the average change in rank, over this 13-year period, is 0.8, from 2.3 to 3.1—that is, less than one rank position. Thus, we conclude, our results are sustainable over time. The main year of this study—2010—does not appear to be unique, as least not with the respect to the past two decades.</p>
<p class="C826"><strong><a id="page_39">1.4 Conclusions and Directions for Future Research</a></strong></p><a id="page_39">
<p class="P51">We began by noting that prior literature establishes that the high level of inequality in US disposable household income, calculated across working-age households, is not only the product of modest redistribution in the US as compared with similar OECD countries; it is also the result of a comparatively high level of inequality in the underlying market income. Furthermore, the primary component of market income is income from labor. In this study, we have shown that equivalized labor income across households is indeed more unequally distributed in the US than in all (but one) of 24 OECD countries included.</p>
<p class="P4">We were also interested in assessing whether labor income inequality is pervasive across household types and demographic subgroups or whether it may be due to either exceptionally high or exceptionally low average labor incomes received by some groups. We conclude that within-group inequality of labor incomes in the US is, in almost all cases, high by OECD standards. So, it is neither an unusual household composition nor unusually high mean labor incomes of some demographic groups that explains high US earnings inequality, but simply the fact that high and low labor incomes are widely spread across all of our household/demographic categories.</p>
<p class="P4">We have seen that, in 2010, when we look at 15 (mutually exclusive) demographic groups, the inequality rankings of the US are consistently high. In 10 out of 15 groups (within-group) inequality places the US among the three most unequal countries out of 24; in three more cases, the US falls among the five most unequal countries. Our overall conclusion, clearly, is that the high level of market income inequality in the US, in cross-national perspective, is found across diverse subgroups.</p>
<p class="P4">A detailed policy analysis is beyond the goals and scope of this chapter, but we offer a few final comments about research that would extend what we have reported here.</p>
</a><p class="P4"><a id="page_39">The large cross-national literature on policies and institutions that shape economic inequality can be divided, in general, into two bodies of work: comparative research on the determinants of earnings inequality and comparative studies on income redistribution. The former literature mainly focuses on regulations and other tools that set floors under earnings (mainly minimum wages) and institutions that shape workers’ bargaining power (mainly unions); these public interventions are increasingly referred to as instruments of “predistribution” (Chwalisz and Diamond 2015; Hacker 2011).<sup></sup></a><span href="#/html/38_Notes#id_40" id="id_39" title="Link target outside of Sample">17</span> The latter literature focuses on the design, mix, and effectiveness of <a id="page_40">the classic tools of redistribution—public income transfers and households’ direct taxes (see Gornick and Smeeding 2018 for a review).</a></p><a id="page_40">
<p class="P4">The study that we report in this chapter turns our attention to the instruments of predistribution. Institutions that affect earnings inequality have received heightened attention in recent years among economists studying income inequality (see the influential policy proposals in Atkinson 2015; see also OECD 2008, 2011, 2015). They have also attracted attention among political scientists, sociologists, labor scholars, and legal analysts (e.g., Alderson and Nielsen 2002; Alexander, Haley-Lock, and Ruan 2015; Anker and Anker 2017; Golden and Wallerstein 2011; Kenworthy 2001).</p>
<p class="P4">A substantial strand in that literature focuses on how, and the extent to which, policies and institutions vary across high-income countries (e.g., Blau and Kahn 2002; Salverda and Checchi 2015). As is well known by now, many studies have indicated that, in the US, minimum wages are low and unions are weak, relative to other high-income countries, especially among OECD countries (see OECD 2015). Furthermore, several studies have concluded that the low minimum wages and weak collective bargaining in the US do, in fact, explain a substantial share of the higher level of earnings inequality in the US (see Gornick and Smeeding 2018 for a review).</p>
<p class="C813">Future work that builds on this chapter might address two issues/questions:</p>
</a><p class="C862"><a id="page_40">(1) Public policies and institutions that shape earnings distributions—such as minimum wages, structures of collective bargaining, and other mechanisms for wage setting, including on the high end—are understood to affect the distribution of individuals’ earnings. Our work focuses on <em>households</em>’ earnings; those are clearly shaped by the earnings of individual household members but also by the ways in which households are formed, vis-à-vis combinations of earners. Little if any research assesses the extent to which these earnings-related policies and institutions shape <em>households</em>’ earnings—either directly or indirectly (by influencing household-level employment behavior or even household formation).<sup></sup></a><span href="#/html/38_Notes#id_42" id="id_41" title="Link target outside of Sample">18</span></p>
<p class="C863">(2) The extensive cross-national literature on the major tools of “predistribution” has not, thus far, focused on their varied effects across subgroups of workers, differentiated by “bundles” of characteristics—much less subgroups of households. Little if any research assesses whether (or how or why) earnings-related policies differentially affect <em>households</em>, when those <a id="page_41">households are distinguished by their earners’ gender, partnership, and/or parenting status.</a></p><a id="page_41">
<p class="P4">A rich and growing supply of institutional databases in combination with high-quality microdata (such as the LIS data)–available both across countries and over time—offers the basis for future studies that might tackle these questions.</p>
<p class="C754"><strong>Appendix</strong></p>
</a><div class="C1136"><a id="page_41">
</a><p class="C1252"><a id="page_41"><strong></strong></a><strong><a id="id_816">Table 1A.1 LIS datasets used</a></strong></p><a id="id_816">
<div class="tablequery">
<table>
<tbody><tr class="Row_Style119"><td class="Col_Style196"></td>
<td class="Col_Style264">Name of survey</td>
<td class="Col_Style264">Year</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Australia</td>
<td class="Col_Style33">Household Expenditure Survey (HES) and Survey of Income and Housing (SIH)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Canada</td>
<td class="Col_Style33">Survey of Labour and Income Dynamics (SLID)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Czech Republic</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU-SILC)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Denmark</td>
<td class="Col_Style33">Statistics Denmark: Law Model</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Estonia</td>
<td class="Col_Style33">Estonian Social Survey (ESS); Survey on Income and Living Conditions (EU-SILC)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Finland</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU-SILC), formerly known as Income Distribution Survey (IDS)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">France</td>
<td class="Col_Style33">Family Budget Survey (BdF)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Germany</td>
<td class="Col_Style33">German Social Economic Panel Study (GSOEP)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Greece</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU-SILC), 2011</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Hungary</td>
<td class="Col_Style33">Household Monitor Survey (HES)</td>
<td class="Col_Style253">2009</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Iceland</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU-SILC)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Ireland</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU-SILC)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Israel</td>
<td class="Col_Style33">Household Expenditure Survey (HES)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Italy</td>
<td class="Col_Style33">Survey of Household Income and Wealth (SHIW)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Luxembourg</td>
<td class="Col_Style33">Panel socio-économique “Liewen zu Letzebuerg” (PSELL III); Survey of Income and Living Conditions (EU-SILC)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Netherlands</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU-SILC)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Norway</td>
<td class="Col_Style33">Household Income Statistics (formerly based on the Income Distribution Survey)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Poland</td>
<td class="Col_Style33">Household Budget Survey</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Russia</td>
<td class="Col_Style33">Russia Longitudinal Monitoring Survey, Higher School of Economics (RLMS-HSE)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Slovakia</td>
<td class="Col_Style33">Survey of Income and Living Conditions (EU SILC), 2011</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Slovenia</td>
<td class="Col_Style33">Household Budget Survey</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">Spain</td>
<td class="Col_Style33">Encuesta de Condiciones de Vida (ECV); Survey of Income and Living Condition (EU-SILC), 2010</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style1"><td class="Col_Style83">United Kingdom</td>
<td class="Col_Style33">Family Resources Survey (FRS)</td>
<td class="Col_Style253">2010</td>
</tr>
<tr class="Row_Style132"><td class="Col_Style83">United States</td>
<td class="Col_Style33">Current Population Survey (CPS) Annual Social and Economic Supplement (ASEC)</td>
<td class="Col_Style253">2010</td>
</tr>
</tbody></table>
</div>
<p class="image"><img alt="" class="tablequery" src="html/images/55_1.png"/></p>
</a></div><a id="id_816">
</a><p class="C754"><a id="id_816"><strong></strong></a><strong><a id="page_42">References</a></strong></p><a id="page_42">
<p class="C745">Alderson, Arthur S., and Francois Nielsen. 2002. “Globalization and the Great U-Turn: Income Inequality Trends in 16 OECD Countries.” <em>American Journal of Sociology</em> 107 (5): 1244–99.</p>
<p class="C745">Alexander, Charlotte, Anna Haley-Lock, and Nantiya Ruan. 2015. “Stabilizing Low-Wage Work.” <em>Harvard Civil Rights-Civil Liberties Law Review</em> 50: 1–48.</p>
<p class="C745">Anker, Richard, and Martha Anker. 2017. <em>Living Wages Around the World: Manual for Measurement</em>. Northampton, MA: Edward Elgar.</p>
<p class="C745">Atkinson, Anthony B. 2015. <em>Inequality: What Can Be Done?</em> Cambridge, MA: Harvard University Press.</p>
<p class="C745">Blau, Francine D., and Lawrence M. Kahn. 2002. <em>At Home and Abroad: U. S. Labor Market Performance in International Perspective</em>. New York: Russell Sage Foundation.</p>
<p class="C745">Blau, Francine D., and Anne E. Winkler. 2017. <em>The Economics of Women, Men, and Work</em>, 8th ed. New York: Oxford University Press.</p>
<p class="C745">Brandolini, Andrea, and Timothy M. Smeeding. 2006. “Patterns of Economic Inequality in Western Democracies: Some Facts on Levels and Trends.” <em>Political Science and Politics</em> 39 (1): 21–26.</p>
<p class="C745">Chwalisz, Claudia, and Patrick Diamond, eds. 2015. <em>The Predistribution Agenda: Tackling Inequality and Supporting Sustainable Growth</em>. London: IB Taurus.</p>
<p class="C745">Gabaix, Xavier, and Augustin Landler. 2008. “Why Has CEO Pay Increased so Much?” <em>Quarterly Journal of Economics</em> 123 (1): 49–100.</p>
<p class="C745">Gautié, Jérôme, and John Schmitt, eds. 2009. <em>Low-Wage Work in the Wealthy World</em>. New York: Russell Sage Foundation.</p>
<p class="C745">Golden, Miriam, and Michael Wallerstein. 2011. “Domestic and International Causes for the Rise of Pay Inequality in OECD Nations between 1980 and 2000. In <em>Comparing European Workers Part A: Experiences and Inequalities</em>, edited by David Brady, 209–49. Bingley: Emerald.</p>
<p class="C745">Gornick, Janet C., and Markus Jäntti, eds. 2013. <em>Income Inequality Economic Disparities and the Middle Class in Affluent Countries</em>. Stanford, CA: Stanford University Press.</p>
</a><p class="C745"><a id="page_42">Gornick, Janet C., and Branko Milanovic. 2015. “Income Inequality in the United States in Cross-National Perspective: Redistribution Revisited.” LIS Center Research Brief 1/2015. New York: Stone Center on Socio-Economic Inequality. </a><a href="https://stonecenter.gc.cuny.edu/research/income-inequality-in-the-united-states-in-cross-national-perspective-redistribution-revisited/" target="_blank">https://stonecenter.gc.cuny.edu/research/income-inequality-in-the-united-states-in-cross-national-perspective-redistribution-revisited/</a>.</p>
<p class="C745">Gornick, Janet C., and Timothy M. Smeeding. 2018. “Redistributional Policy in Rich Countries: Institutions and Impacts in Nonelderly Households.” <em>Annual Review of Sociology</em> 44: 441–68.</p>
<p class="C745">Hacker, Jacob. 2011. “The Institutional Foundations of Middle-Class Democracy.” <em>Policy Network</em>. 6 (5): 33–37.</p>
<p class="C745">Kenworthy, Lane. 2001. “Wage-Setting Measures: A Survey and Assessment.” <em>World Politics</em> 54: 57–98.</p>
<p class="C745">Lemieux, Thomas. 2006. “Increasing Residual Wage Inequality: Composition Effects, Noisy Data, or Rising Demand for Skill?” <em>American Economic Review</em> 96 (3): 461–98.</p>
<p class="C745">Lucifora, Claudio, and Wiemer Salverda W. 2009. “Low Pay.” In <em>The Oxford Handbook of Economic Inequality</em>, edited by Brian Nolan, Wiemer Salverda, and Timothy M. Smeeding, 257–83. Oxford: Oxford University Press.</p>
<p class="C745"><a id="page_43">Luxembourg Income Study (LIS) Database. </a><a href="http://www.lisdatacenter.org" target="_blank">http://www.lisdatacenter.org</a>. Luxembourg: Luxembourg Income Study Center.</p>
<p class="C745">McCall, Leslie. 2000. “Explaining Levels of Within-Group Wage Inequality in U.S. Labor Markets.” <em>Demography</em> 37 (4): 415–30.</p>
<p class="C745">Mishel, Lawrence. 2015. “Causes of Wage Stagnation.” Economic Policy Institute, January 6. <a href="https://www.epi.org/publication/causes-of-wage-stagnation/" target="_blank">https://www.epi.org/publication/causes-of-wage-stagnation/</a>.</p>
<p class="C745">Mishel, Lawrence, and Alyssa Davis. 2015. “Top CEOs Make 300 Times More than Typical Workers.” Economic Policy Institute Issue Brief No. 399. <a href="http://www.epi.org/files/2015/top-ceos-make-300-times-more-than-typical-workers.pdf" target="_blank">http://www.epi.org/files/2015/top-ceos-make-300-times-more-than-typical-workers.pdf</a>.</p>
<p class="C745">Organization for Economic Cooperation and Development (OECD). 2008. <em>Growing Unequal: Income Distribution and Poverty in OECD Countries</em>. Paris: OECD.</p>
<p class="C745">Organization for Economic Cooperation and Development (OECD). 2011. <em>Divided We Stand: Why Inequality Keeps Rising</em>. Paris: OECD.</p>
<p class="C745">Organization for Economic Cooperation and Development (OECD). 2015. <em>Minimum Wages After the Crisis: Making Them Pay</em>. Paris: OECD</p>
<p class="C745">Piketty, Thomas. 2014. <em>Capital in the Twenty-First Century</em>. Translated by Arthur Goldhammer. Cambridge, MA: Belknap Press of Harvard University Press.</p>
<p class="C745">Piketty, Thomas, and Emmanuel Saez. 2006. “The Evolution of Top Incomes: A Historical and International Perspective.” <em>American Economic Review</em> 96 (2): 200–205.</p>
<p class="C745">Salverda, Wiemer, and Daniele Checchi. 2015. “Labour-Market Institutions and the Dispersion of Wage Earnings.” In <em>Handbook of Income Distribution</em>, edited by Anthony B. Atkinson and François Bourguignon, 2:1535–727. Amsterdam: Elsevier.</p>
<p class="C745">VanHeuveline, Tom. 2018. “Recovering the Missing Middle: A Mesocomparative Analysis of Within-Group Inequality, 1970–2011.” <em>American Journal of Sociology</em> 123 (4): 1064–116.</p>
<p class="C745">Western, Bruce, Deirdre Bloome, and Christine Percheski. 2008. “Inequality among American Families with Children, 1975 to 2005.” <em>American Sociological Review</em> 73 (6): 903–20.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 11_1__In_Search_of_the_Roots_of_Ame.vue?0add7c52